"use client";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Clock, Layers, Wrench } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const exampleCourses = [
  {
    title: "Foundations: Prep, Base Coats & First Finish",
    description:
      "Start with the essentials: substrate evaluation, proper prep, base coats, and a clean first finish you can repeat on site.",
    cover: {
      src: "/images/plaster-samples/light-taupe.jpeg",
      alt: "Smooth plaster finish in a light taupe tone",
    },
    meta: {
      level: "Beginner",
      duration: "~90 min",
      focus: "Technique",
    },
    takeaways: [
      "Moisture + surface readiness checklist",
      "Base coat consistency and trowel control",
      "Avoiding common early-stage failures",
    ],
    icon: Layers,
  },
  {
    title: "Detailing: Corners, Transitions & Repairs",
    description:
      "Make your work look intentional. Learn crisp corners, seamless transitions, and fast, invisible repairs when things go sideways.",
    cover: {
      src: "/images/plaster-samples/gray-wool.jpeg",
      alt: "Textured plaster finish in a neutral gray tone",
    },
    meta: {
      level: "Intermediate",
      duration: "~75 min",
      focus: "Finish quality",
    },
    takeaways: [
      "Corners and edge control without chatter",
      "Touch-ups: sheen matching and blending",
      "Fixing burn marks, lines, and pinholes",
    ],
    icon: Wrench,
  },
  {
    title: "Jobsite Workflow: Estimating, Scope & Handoff",
    description:
      "Turn skills into a repeatable process: bids, scope, client expectations, and a handoff that reduces callbacks and protects your margin.",
    cover: {
      src: "/images/plaster-samples/brick.jpeg",
      alt: "Materials and texture reference for jobsite planning",
    },
    meta: {
      level: "Pro",
      duration: "~60 min",
      focus: "Business",
    },
    takeaways: [
      "Scope language that prevents surprises",
      "Estimating time, materials, and risk",
      "Client care: maintenance + final walk",
    ],
    icon: Clock,
  },
] as const;

export function ProductShowcaseSection() {
  return (
    <section
      id="home-product-showcase"
      className="py-24 bg-foreground/5"
      data-header-scheme="light"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <Badge className="mb-5 bg-accent text-accent-foreground px-4 py-2 font-medium">
            Courses + Videos
          </Badge>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-5 tracking-tight">
            Example Courses
          </h2>
          <p className="font-sans text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            A preview of the curriculum: short, jobsite-focused lessons that
            build real technique and a repeatable workflow.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {exampleCourses.map((course, index) => {
            const Icon = course.icon;
            return (
              <motion.div
                key={course.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden bg-card/70 backdrop-blur-sm py-0 gap-0">
                  <div className="relative aspect-video bg-muted">
                    <Image
                      src={course.cover.src}
                      alt={course.cover.alt}
                      fill
                      sizes="(max-width: 768px) 100vw, 33vw"
                      className="object-cover"
                    />
                    <div className="absolute inset-0 bg-linear-to-t from-black/35 via-black/0 to-black/0" />
                  </div>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4 pt-4">
                      <div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Icon className="h-4 w-4" aria-hidden="true" />
                          <span className="text-xs uppercase tracking-wider">
                            {course.meta.focus}
                          </span>
                        </div>
                        <CardTitle className="mt-3 font-serif text-2xl font-light tracking-tight">
                          {course.title}
                        </CardTitle>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Badge variant="secondary">{course.meta.level}</Badge>
                      <Badge variant="secondary">{course.meta.duration}</Badge>
                    </div>
                    <CardDescription className="mt-4 text-base leading-relaxed">
                      {course.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 pb-4">
                      {course.takeaways.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-3 text-sm text-muted-foreground"
                        >
                          <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />
                          <span className="leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter className="justify-between border-t pt-2! pb-2">
                    <span className="text-xs text-muted-foreground">
                      Included with early access
                    </span>
                    <Button variant="ghost" size="sm" asChild>
                      <Link href="/courses#presignup">
                        Pre-sign up <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 flex justify-center">
          <Button size="lg" asChild>
            <Link href="/courses#presignup">
              See Courses Roadmap <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
